import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useModal } from '../../contexts/ModalContext';

import { DEFAULT_SEMESTER } from '../../constants';
import { AddableCourse, CourseLite, CourseOption } from '../../types/course.response';
import { MutationEnvelope } from '../../types/mutation.response';
import { CatalogProgram } from '../../types/catalog.response';
import { programsApi } from '../../services/api/program.api';
import { userCoursesApi } from '../../services/api/user-courses.api';
import { userExamsApi } from '../../services/api/user-exams.api';
import { catalogApi } from '../../services/api/catalog.api';
import { CourseRequirementOverride } from '../../services/api/user-courses.api';
import { useCourseActions } from '../../hooks/useCourseActions';
import { useSlicePrefetch } from '../../hooks/useSlicePrefetch';
import { programSlices } from '../../services/programSlices';
import { reconcileUserItems } from '../../services/userCourseCache';
import { ensureSliceCacheIdentity } from '../../services/sliceCacheIdentity';
import RequirementSection from '../../components/requirement-section/RequirementSection';
import RequirementSectionPlaceholder from '../../components/requirement-section/RequirementSectionPlaceholder';
import SearchableDropdown from '../../components/shared/SearchableDropdown';
import RequirementOverlay from '../../components/requirement-overlay/RequirementOverlay';
import ProgramSkeleton from '../../components/loading/ProgramSkeleton';
import HomeSidebarGuest from './HomeSidebarGuest';
import HomeSidebarLoggedIn, { ProgramLoadState } from './HomeSidebarLoggedIn';
import { isRequirementFulfilled } from '../../utils/requirementResponseUtils';
import { getSemesterName } from '../../utils/semesterUtils';

/** skeleton 一到就发的首批切片数 = 编排器一个批次;其余由视口预取驱动 */
const FIRST_BATCH_SIZE = 3;


export default function Home() {
    const { isLoggedIn, authLoading, user } = useAuth();
    const { openLoginModal } = useModal();

    // Logged-in state:program 数据由切片编排器(programSlices)持有,订阅版本号派生快照
    const sliceVersion = useSyncExternalStore(programSlices.subscribe, programSlices.getVersion);
    const [userCourses, setUserCourses] = useState<CourseOption[]>([]);
    const [userCoursesLoaded, setUserCoursesLoaded] = useState(false);
    const [optimisticFavorites, setOptimisticFavorites] = useState<Map<string, boolean>>(new Map());
    const [openRequirement, setOpenRequirement] = useState<{ programId: string; requirementId: string } | null>(null);

    // Guest state
    const [allCatalogPrograms, setAllCatalogPrograms] = useState<CatalogProgram[]>([]);
    const [collegePrograms, setCollegePrograms] = useState<CatalogProgram[]>([]);
    const [selectedCollegeId, setSelectedCollegeId] = useState<string>('');
    const [quickFinderCollegeId, setQuickFinderCollegeId] = useState<string>('');
    const [quickFinderProgramId, setQuickFinderProgramId] = useState<string>('');

    // Logged-in:skeleton-first(契约 §3.1)。skeleton 由编排器并行拉取;
    // refreshFulfillments 兜住跨页改课后的缓存过期(Program 页接入编排器前的口子),
    // 同时承担 userCourses/exams 的首次拉取。
    useEffect(() => {
        if (authLoading || !isLoggedIn || !user) return;

        ensureSliceCacheIdentity(`user:${user.id}`);
        user.programs.forEach(({ programId }) => programSlices.ensureProgram(programId));
        refreshFulfillments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authLoading, isLoggedIn, user]);

    // 当前页面消费的 program 集合:登录态 = 用户的全部 program,guest = 选中的 college
    const activeProgramIds = useMemo(() => {
        if (authLoading) return [];
        if (isLoggedIn) return (user?.programs ?? []).map(p => p.programId);
        return selectedCollegeId ? [selectedCollegeId] : [];
    }, [authLoading, isLoggedIn, user, selectedCollegeId]);

    // skeleton 一到就发该 program 的首批切片(展示顺序前 3 个未完成 requirement;
    // guest 的匿名 summaries 零完成,等价于全部)。后续批次由占位区块的视口预取驱动,不在这里全量入队
    useEffect(() => {
        activeProgramIds.forEach(programId => {
            const snap = programSlices.getSnapshot(programId);
            if (snap?.status !== 'loaded' || !snap.program) return;
            const firstBatch = snap.program.requirements
                .filter(r => !isRequirementFulfilled(r) && snap.hasAvailableCourses[r.info.id])
                .slice(0, FIRST_BATCH_SIZE)
                .map(r => r.info.id)
                .filter(id => snap.sliceStatus[id] === 'idle');
            if (firstBatch.length > 0) {
                programSlices.requestSlices(programId, firstBatch);
            }
        });
    }, [activeProgramIds, sliceVersion]);

    // 占位区块的视口预取(root = 左列滚动容器;guest/登录视图切换时容器重建,重挂 observer)
    const scrollRootRef = useRef<HTMLDivElement | null>(null);
    const registerPlaceholder = useSlicePrefetch(scrollRootRef, [authLoading, isLoggedIn]);

    // Guest: fetch all programs from catalog
    useEffect(() => {
        if (authLoading || isLoggedIn) return;
        catalogApi.getPrograms()
            .then(programs => {
                setAllCatalogPrograms(programs);
                const colleges = programs
                    .filter(p => p.type === 'college')
                    .sort((a, b) => a.name.localeCompare(b.name));
                setCollegePrograms(colleges);
                if (colleges.length > 0) {
                    setSelectedCollegeId(colleges[0].programId);
                }
            })
            .catch(console.error);
    }, [authLoading, isLoggedIn]);

    // Guest:skeleton-first,复用同一编排器(匿名 skeleton summaries 零完成,全部视为未完成,
    // 契约 §3.1)。切换 college / 登入 / 离开页面时移除该 program,在途批次因 entry 替换自动作废
    useEffect(() => {
        if (authLoading || isLoggedIn || !selectedCollegeId) return;
        ensureSliceCacheIdentity('guest');
        programSlices.ensureProgram(selectedCollegeId);
        return () => programSlices.clearProgram(selectedCollegeId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authLoading, isLoggedIn, selectedCollegeId]);

    const navigate = useNavigate();

    // Quick finder derived data
    const quickFinderColleges = useMemo(() => {
        const collegeMap = new Map<string, { collegeId: string; name: string }>();
        allCatalogPrograms
            .filter(p => p.type !== 'college')
            .flatMap(p => p.colleges)
            .forEach(c => collegeMap.set(c.collegeId, c));
        return Array.from(collegeMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [allCatalogPrograms]);

    const quickFinderPrograms = useMemo(() => {
        if (!quickFinderCollegeId) return [];
        return allCatalogPrograms
            .filter(p => p.type !== 'college' && p.colleges.some(c => c.collegeId === quickFinderCollegeId))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [allCatalogPrograms, quickFinderCollegeId]);

    // Reset program selection when college changes
    useEffect(() => {
        const first = quickFinderPrograms[0];
        setQuickFinderProgramId(first ? first.programId : '');
    }, [quickFinderCollegeId, quickFinderPrograms]);

    // Set default quick finder college once programs are loaded
    useEffect(() => {
        if (quickFinderColleges.length > 0 && !quickFinderCollegeId) {
            setQuickFinderCollegeId(quickFinderColleges[0].collegeId);
        }
    }, [quickFinderColleges]);

    /** 编排器快照 → 旧 ProgramLoadState 形状(sidebar / overlay 消费),随 sliceVersion 重新派生 */
    const programStates = useMemo(() => {
        const map = new Map<string, ProgramLoadState>();
        (user?.programs ?? []).forEach(({ programId }) => {
            const snap = programSlices.getSnapshot(programId);
            if (!snap) return;
            map.set(programId, { status: snap.status, data: snap.program, error: snap.error });
        });
        return map;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, sliceVersion]);

    // stateVersion 落地前的乱序保护:只应用最新一次发起的刷新结果
    //(stateVersion 在场时由编排器 applyFulfillment 内部兜底)
    const refreshSeqRef = useRef(0);

    // 改课后的轻量刷新:静默 patch 已加载 program 的合并对象,不显示全屏遮罩;
    // 结构漂移由编排器内部失效重拉兜底。userCourses 的首次拉取也走这里(挂载 effect)。
    // outcome(契约 §4):meta 在场时收窄到 affectedProgramIds ∩ 已加载,
    // 其余卡片数据未变;内嵌详情档在场的 program 直接用,省掉 GET fulfillment。
    const refreshFulfillments = async (outcome?: MutationEnvelope) => {
        const loadedIds = (user?.programs ?? [])
            .map(p => p.programId)
            .filter(id => programSlices.getSnapshot(id)?.status === 'loaded');
        const targetIds = outcome?.meta
            ? loadedIds.filter(id => outcome.meta!.affectedProgramIds.includes(id))
            : loadedIds;
        const embedded = new Map((outcome?.fulfillments ?? []).map(f => [f.programId, f]));

        const seq = ++refreshSeqRef.current;
        try {
            const [courses, exams, results] = await Promise.all([
                userCoursesApi.getCourses(),
                userExamsApi.getExams(),
                Promise.allSettled(targetIds.map(id =>
                    embedded.get(id) ?? programsApi.getProgramFulfillment(id))),
            ]);
            if (seq !== refreshSeqRef.current) return; // 已有更新的刷新,丢弃

            const userItems = [...courses, ...exams];
            // §3.4-c 双读对拍:userStates 覆盖全部用户课,任一详情档都可作基准
            const reference = results.find(r => r.status === 'fulfilled');
            if (reference?.status === 'fulfilled') reconcileUserItems(reference.value.userStates, userItems);
            targetIds.forEach((programId, i) => {
                const result = results[i];
                if (result.status === 'rejected') {
                    console.error(`Error refreshing fulfillment for ${programId}:`, result.reason);
                    return; // 保留旧数据
                }
                programSlices.applyFulfillment(programId, result.value, userItems);
            });
            setUserCourses(courses);
            setUserCoursesLoaded(true);
        } catch (err) {
            console.error('Error refreshing fulfillments:', err);
        }
    };

    const courseActions = useCourseActions({
        isLoggedIn,
        openLoginModal,
        userCourses,
        optimisticFavorites,
        setOptimisticFavorites,
        onSuccess: () => { /* Home refreshes in the curried wrappers below(拿返回的 outcome 收窄范围) */ },
        fulfillmentProgramIds: () => programSlices.getLoadedProgramIds(),
        rethrow: true,
    });

    const handleAddCourse = (programId: string) => async (
        courseOption: AddableCourse,
        overrides: CourseRequirementOverride[],
        semester: string | null,
        classSectionIds?: number[],
    ) => {
        if (!isLoggedIn) { openLoginModal(); return; }
        const outcome = await courseActions.handleAddCourse(courseOption, overrides, semester, classSectionIds);
        await refreshFulfillments(outcome);
    };

    const handleDeleteCourse = (programId: string) => async (deletedCourse: CourseLite) => {
        if (!isLoggedIn) { openLoginModal(); return; }
        const outcome = await courseActions.handleDeleteCourse(deletedCourse);
        await refreshFulfillments(outcome);
    };

    const handleFavoriteCourse = courseActions.handleFavoriteCourse;

    const handleOverride = (programId: string) => async (
        course: CourseLite,
        override: CourseRequirementOverride
    ) => {
        if (!isLoggedIn) { openLoginModal(); return; }
        const userItemId = course.userItemId;
        if (!userItemId) return;
        try {
            const fulfillmentIds = programSlices.getLoadedProgramIds();
            const outcome = course.type === 'EXAM'
                ? await userExamsApi.addOverride(userItemId, override, fulfillmentIds)
                : await userCoursesApi.addOverride(userItemId, override, fulfillmentIds);
            await refreshFulfillments(outcome);
        } catch (err) {
            console.error('Error updating override:', err);
            throw err;
        }
    };

    if (authLoading) return null;

    // Guest view
    if (!isLoggedIn) {
        const programCount = allCatalogPrograms.filter(p => p.type !== 'college').length;
        const guestSnap = selectedCollegeId ? programSlices.getSnapshot(selectedCollegeId) : undefined;
        const guestProgram = guestSnap?.status === 'loaded' ? guestSnap.program : undefined;
        const guestRequirements = guestProgram
            ? guestProgram.requirements.filter(r => guestSnap!.hasAvailableCourses[r.info.id])
            : [];
        const guestCollegeName = collegePrograms.find(p => p.programId === selectedCollegeId)?.name ?? '';

        return (
            <>
                <div className="mt-2 h-[calc(100vh-4rem)] overflow-hidden">
                    <div className="w-full max-w-[1200px] mx-auto px-4 md:px-12 h-full">
                        <div className="pt-4 grid grid-cols-1 md:grid-cols-[1fr_312px] gap-6 h-full min-h-0">
                            {/* Left column */}
                            <div ref={scrollRootRef} className="w-full min-w-0 pb-20 overflow-y-auto min-h-0">
                                {/* Hero + Program Selector */}
                                {quickFinderColleges.length > 0 && (
                                    <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 md:p-8">
                                        <h1 className="text-xl md:text-2xl font-bold text-white mb-1.5">
                                            Find the courses you still need
                                        </h1>
                                        <div className="flex flex-col md:flex-row gap-3 md:items-end">
                                            <div className="flex-1">
                                                <label className="text-gray-400 text-sm font-medium mb-1.5 block">College</label>
                                                <SearchableDropdown
                                                    value={quickFinderCollegeId}
                                                    options={quickFinderColleges.map(c => ({ _id: c.collegeId, name: c.name }))}
                                                    onChange={setQuickFinderCollegeId}
                                                    excludeIds={[]}
                                                    placeholder="Search for a college..."
                                                    noResultsText="No colleges found"
                                                    variant="dark"
                                                />
                                            </div>
                                            <div className="flex-1">
                                                <label className="text-gray-400 text-sm font-medium mb-1.5 block">Program</label>
                                                <SearchableDropdown
                                                    value={quickFinderProgramId}
                                                    options={quickFinderPrograms.map(p => ({ _id: p.programId, name: p.name + (p.type === 'minor' ? ' Minor' : '') }))}
                                                    onChange={setQuickFinderProgramId}
                                                    excludeIds={[]}
                                                    placeholder="Search for a program..."
                                                    noResultsText="No programs found"
                                                    variant="dark"
                                                />
                                            </div>
                                            <button
                                                onClick={() => navigate(`/programs/${quickFinderProgramId}`)}
                                                disabled={!quickFinderProgramId}
                                                className="w-full md:w-auto rounded-lg bg-white text-blue-700 font-semibold px-6 py-2.5 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                            >
                                                View Program →
                                            </button>
                                        </div>
                                        {programCount > 0 && (
                                            <p className="text-gray-500 text-sm mt-3">
                                                {programCount} majors and minors available{' · '}
                                                <Link to="/programs" className="text-gray-300 underline hover:text-white">view all</Link>
                                            </p>
                                        )}
                                    </div>
                                )}

                                {/* Available courses - desktop only */}
                                <div className="hidden md:block mt-8">
                                    <h2 className="text-lg md:text-2xl font-bold text-blue-700 mb-6">
                                        Available courses for{' '}
                                        {collegePrograms.length > 0 && (
                                            <select
                                                value={selectedCollegeId}
                                                onChange={e => setSelectedCollegeId(e.target.value)}
                                                className="font-bold text-blue-700 bg-transparent border-b-2 border-dashed border-yellow-300 appearance-none cursor-pointer focus:outline-none focus:border-yellow-500 pr-1"
                                            >
                                                {collegePrograms.map(p => (
                                                    <option key={p.programId} value={p.programId}>{p.name}</option>
                                                ))}
                                            </select>
                                        )}
                                    </h2>

                                    {(!guestSnap || guestSnap.status === 'loading') && <ProgramSkeleton />}
                                    {guestSnap?.status === 'error' && (
                                        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                                            Failed to load {guestCollegeName}
                                        </div>
                                    )}
                                    {guestProgram && guestRequirements.map(requirement => (
                                        <div key={requirement.info.id} className="bg-white mb-2">
                                            {guestSnap!.sliceStatus[requirement.info.id] === 'loaded' ? (
                                                <RequirementSection
                                                    program={guestProgram}
                                                    requirement={requirement}
                                                    handleAddCourse={handleAddCourse(selectedCollegeId)}
                                                    handleDeleteCourse={handleDeleteCourse(selectedCollegeId)}
                                                    handleFavoriteCourse={handleFavoriteCourse}
                                                    handleOverride={handleOverride(selectedCollegeId)}
                                                    optimisticFavorites={optimisticFavorites}
                                                    userCourses={userCourses}
                                                    fromHome
                                                    onRequirementClick={(pId, rId) => setOpenRequirement({ programId: pId, requirementId: rId })}
                                                />
                                            ) : (
                                                <RequirementSectionPlaceholder
                                                    program={guestProgram}
                                                    requirement={requirement}
                                                    handleAddCourse={handleAddCourse(selectedCollegeId)}
                                                    handleDeleteCourse={handleDeleteCourse(selectedCollegeId)}
                                                    handleOverride={handleOverride(selectedCollegeId)}
                                                    fromHome
                                                    onRequirementClick={(pId, rId) => setOpenRequirement({ programId: pId, requirementId: rId })}
                                                    onRegister={registerPlaceholder}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {/* Features - mobile only */}
                                <div className="md:hidden space-y-4 mt-10 mb-10">
                                    <div className="flex items-start gap-3">
                                        <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">Track progress</p>
                                            <p className="text-sm text-gray-500">See what you've completed and what's left</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">Find courses</p>
                                            <p className="text-sm text-gray-500">Courses available for your remaining requirements</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <svg className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">Build your schedule</p>
                                            <p className="text-sm text-gray-500">Plan your semester without time conflicts</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Login CTA - mobile only */}
                                <div className="md:hidden">
                                    <button
                                        onClick={openLoginModal}
                                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl py-3.5 text-base transition-colors"
                                    >
                                        Get Started
                                    </button>
                                </div>
                            </div>

                        {/* Right column: sidebar - desktop only */}
                        <div className="hidden md:block sticky top-4 self-start">
                                <HomeSidebarGuest />
                            </div>
                        </div>
                    </div>
                </div>
                {openRequirement && (
                    <RequirementOverlay
                        programId={openRequirement.programId}
                        requirementId={openRequirement.requirementId}
                        onClose={() => setOpenRequirement(null)}
                        onDataChanged={() => programSlices.invalidateProgram(selectedCollegeId)}
                        userCourses={userCourses}
                    />
                )}
            </>
        );
    }

    // Logged-in view
    const userPrograms = user!.programs;

    return (
        <>
            <div className="mt-2 h-[calc(100vh-4rem)] overflow-hidden">
                <div className="w-full max-w-[1200px] mx-auto px-4 md:px-12 h-full">
                    <div className="pt-4 grid grid-cols-1 md:grid-cols-[1fr_312px] gap-6 h-full min-h-0">
                        {/* Left column: program sections */}
                        <div ref={scrollRootRef} className="w-full min-w-0 pb-20 overflow-y-auto min-h-0">
                            {userCoursesLoaded && userCourses.length === 0 && (
                                <button
                                    onClick={() => navigate('/plan')}
                                    className="w-full text-left bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 mb-6 hover:from-gray-700 hover:to-gray-800 transition-colors group"
                                >
                                    <p className="text-gray-400 text-xs md:text-sm font-medium mb-1 uppercase tracking-widest">Get started</p>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h2 className="text-white text-lg md:text-2xl font-bold mb-1">Add courses you have completed</h2>
                                            <p className="text-gray-400 text-xs md:text-sm">Track your progress and see what's left for your programs</p>
                                        </div>
                                        <span className="text-white text-lg md:text-2xl ml-6 flex-shrink-0 group-hover:translate-x-1 transition-transform">→</span>
                                    </div>
                                </button>
                            )}
                            <h1 className="text-lg md:text-2xl font-bold text-blue-700 mb-6">
                                See what you can take in {getSemesterName(DEFAULT_SEMESTER)}
                            </h1>
                            {userPrograms.map(({ programId, name }) => {
                                const snap = programSlices.getSnapshot(programId);

                                if (!snap || snap.status === 'loading') {
                                    return (
                                        <div key={programId}>
                                            <ProgramSkeleton />
                                        </div>
                                    );
                                }

                                if (snap.status === 'error') {
                                    return (
                                        <div key={programId} className="mb-8">
                                            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                                                Failed to load {name}
                                            </div>
                                        </div>
                                    );
                                }

                                const program = snap.program!;
                                const visibleRequirements = program.requirements
                                    .filter(r => !isRequirementFulfilled(r))
                                    .filter(r => snap.hasAvailableCourses[r.info.id]);

                                if (visibleRequirements.length === 0) return null;

                                return (
                                    <div key={programId}>
                                        {visibleRequirements.map(requirement => (
                                            <div key={requirement.info.id} className="bg-white mb-2">
                                                <Link
                                                    to={`/programs/${programId}`}
                                                    className="flex items-center gap-2 mb-1 w-fit hover:opacity-75 transition-opacity"
                                                >
                                                    <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                                                        <span className="text-[10px] font-bold text-gray-600">
                                                            {program.info.id.substring(0, 2).toUpperCase()}
                                                        </span>
                                                    </div>
                                                    <span className="text-xs font-medium text-gray-700">
                                                        {program.info.name}
                                                    </span>
                                                </Link>
                                                {snap.sliceStatus[requirement.info.id] === 'loaded' ? (
                                                    <RequirementSection
                                                        program={program}
                                                        requirement={requirement}
                                                        handleAddCourse={handleAddCourse(programId)}
                                                        handleDeleteCourse={handleDeleteCourse(programId)}
                                                        handleFavoriteCourse={handleFavoriteCourse}
                                                        handleOverride={handleOverride(programId)}
                                                        optimisticFavorites={optimisticFavorites}
                                                        userCourses={userCourses}
                                                        fromHome
                                                        onRequirementClick={(pId, rId) => setOpenRequirement({ programId: pId, requirementId: rId })}
                                                    />
                                                ) : (
                                                    <RequirementSectionPlaceholder
                                                        program={program}
                                                        requirement={requirement}
                                                        handleAddCourse={handleAddCourse(programId)}
                                                        handleDeleteCourse={handleDeleteCourse(programId)}
                                                        handleOverride={handleOverride(programId)}
                                                        fromHome
                                                        onRequirementClick={(pId, rId) => setOpenRequirement({ programId: pId, requirementId: rId })}
                                                        onRegister={registerPlaceholder}
                                                    />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Right column: sidebar */}
                        <div className="hidden md:block sticky top-4 self-start">
                            <HomeSidebarLoggedIn
                                userPrograms={userPrograms}
                                programStates={programStates}
                            />
                        </div>
                    </div>
                </div>
            </div>
            {openRequirement && (
                <RequirementOverlay
                    programId={openRequirement.programId}
                    requirementId={openRequirement.requirementId}
                    onClose={() => setOpenRequirement(null)}
                    onDataChanged={() => refreshFulfillments()}
                    userCourses={userCourses}
                />
            )}
        </>
    );
}
